import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ordo/ui/card'
import { Separator } from '@ordo/ui/separator'
import { Skeleton } from '@ordo/ui/skeleton'
import { BackLink } from 'compositions/BackLink'
import { Page } from 'compositions/Page'
import { useInboundForwarding, useUpdateInboundForwarding } from 'hooks/api/useInbound'
import { FetchApiError } from 'hooks/useFetchApi'
import { Save } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { useParams } from 'react-router'
import type { InboundForwarding } from 'types/inbound'

import { isInboundDestinationValid } from '../../lib/inbound'
import { ForwardingDestinationFields } from './inbound-forwarding-fields'
import { InboundMessages, InboundMessagesSkeleton } from './inbound-messages'
import { InboundError, RecognizedOnlyField } from './inbound-shared'

export function AdminInboundDetail() {
  const { id } = useParams<{ id: string }>()
  const forwarding = useInboundForwarding(id)
  // A retained query payload belongs to its original handler until the new request completes.
  const row = forwarding.data?.id === id ? forwarding.data : null
  const notFound = forwarding.error instanceof FetchApiError && forwarding.error.status === 404

  return (
    <Page>
      <BackLink to="/inbound">Back to Inbound</BackLink>
      {notFound ? (
        <p className="py-12 text-center text-muted-foreground">Handler not found</p>
      ) : forwarding.error && !row ? (
        <InboundError error={forwarding.error} />
      ) : row ? (
        <div key={row.id} className="space-y-6">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="break-words text-xl font-semibold">{row.phoneNumber}</h1>
            <Badge variant={row.active ? 'secondary' : 'outline'}>{row.active ? 'Active' : 'Archived'}</Badge>
          </div>
          {forwarding.error ? <InboundError error={forwarding.error} /> : null}
          <HandlerConfiguration key={`${row.id}-${row.updatedAt}`} forwarding={row} />
          <InboundMessages forwardingId={row.id} />
        </div>
      ) : (
        <div className="space-y-6" role="status" aria-label="Loading handler">
          <Skeleton className="h-7 w-56 max-w-full" />
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-36" />
              <Skeleton className="h-4 w-full max-w-xl" />
            </CardHeader>
            <Separator />
            <CardContent className="space-y-5 pt-6">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-10 w-full max-w-lg" />
              <div className="flex justify-end gap-2">
                <Skeleton className="h-9 w-32" />
                <Skeleton className="h-9 w-20" />
              </div>
            </CardContent>
          </Card>
          <InboundMessagesSkeleton />
        </div>
      )}
    </Page>
  )
}

function HandlerConfiguration({ forwarding }: { forwarding: InboundForwarding }) {
  const [destinationUrl, setDestinationUrl] = useState(forwarding.destinationUrl)
  const [audience, setAudience] = useState(forwarding.audience)
  const [recognizedOnly, setRecognizedOnly] = useState(forwarding.recognizedOnly)
  const [error, setError] = useState<unknown>(null)
  const update = useUpdateInboundForwarding()
  const changed =
    destinationUrl !== forwarding.destinationUrl ||
    audience !== forwarding.audience ||
    recognizedOnly !== forwarding.recognizedOnly
  const validDestination = isInboundDestinationValid(destinationUrl.trim(), audience.trim())
  const save = async (active?: boolean) => {
    setError(null)
    try {
      await update.perform(
        forwarding.id,
        active === undefined
          ? { destinationUrl: destinationUrl.trim(), audience: audience.trim(), recognizedOnly }
          : { active },
      )
    } catch (failure) {
      setError(failure)
    }
  }
  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (changed && validDestination && !update.loading) {
      void save()
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Configuration</CardTitle>
        <CardDescription>Choose where messages received on this number are forwarded.</CardDescription>
      </CardHeader>
      <Separator />
      <CardContent className="pt-6">
        <form className="space-y-5" onSubmit={submit}>
          <ForwardingDestinationFields
            idPrefix="inbound-destination"
            destinationUrl={destinationUrl}
            audience={audience}
            onDestinationUrlChange={setDestinationUrl}
            onAudienceChange={setAudience}
            disabled={update.loading}
          />
          <RecognizedOnlyField
            id="inbound-recognized"
            checked={recognizedOnly}
            onChange={setRecognizedOnly}
            disabled={update.loading}
          />
          {error ? <InboundError error={error} /> : null}
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={update.loading}
              onClick={() => void save(!forwarding.active)}>
              {forwarding.active ? 'Archive Handler' : 'Restore Handler'}
            </Button>
            <Button type="submit" disabled={update.loading || !changed || !validDestination}>
              <Save className="h-4 w-4" /> Save
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
